import { useEffect, useState, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

const API_BASE = import.meta.env.VITE_API_BASE || "https://ops.growtify.app";
const API_TOKEN = import.meta.env.VITE_API_TOKEN || "ops-growtify-2026";

export type SSEStatus = "connecting" | "connected" | "reconnecting" | "disconnected" | "error";

// Friendly toast text per (table, action)
function describeEvent(payload: any): { title: string; description?: string } | null {
  if (!payload || typeof payload !== "object") return null;
  const t = payload.table;
  const a = payload.action;
  if (t === "approvals" && a === "approved") {
    return {
      title: "Approval approved",
      description: payload.actor ? `by ${payload.actor}` : undefined,
    };
  }
  if (t === "approvals" && a === "rejected") {
    return {
      title: "Approval rejected",
      description: payload.actor ? `by ${payload.actor}` : undefined,
    };
  }
  if (t === "input_requests" && a === "upsert") {
    return {
      title:
        payload.status === "awaiting_input"
          ? "New CEO input request"
          : `Input request ${payload.status}`,
      description: payload.tenant ? `tenant: ${payload.tenant}` : undefined,
    };
  }
  if (t === "revenue_ledger" && a === "entry_added") {
    return {
      title: "Revenue entry added",
      description: payload.client_id || payload.tenant
        ? `client: ${payload.client_id || payload.tenant}`
        : undefined,
    };
  }
  if (t === "vendor_ledger" && a === "entry_added") {
    return {
      title: "Vendor entry added",
      description: payload.vendor_id ? payload.vendor_id : undefined,
    };
  }
  if (t === "contractor_ledger" && a === "entry_added") {
    return { title: "Contractor entry added" };
  }
  if (t === "infra_ledger" && a === "entry_added") {
    return { title: "Infra entry added" };
  }
  if (t === "cross_dept_requests" && a === "upsert") {
    const route = payload.route ? ` · ${payload.route}` : "";
    return {
      title: `Routing decision${route}`,
      description: payload.request_id || payload.tenant,
    };
  }
  if (t === "artifacts" && a === "upsert") {
    return {
      title: `Artifact ${payload.status || "updated"}`,
      description: payload.tenant ? `tenant: ${payload.tenant}` : undefined,
    };
  }
  return null;
}

// Map a (table, action) pair to the React-Query keys we need to invalidate.
// Keep this conservative — over-invalidating is cheap (refetch on next poll
// boundary anyway), under-invalidating causes stale screens.
function affectedQueryKeys(payload: any): string[][] {
  const keys: string[][] = [["dashboard"]];
  if (!payload || typeof payload !== "object") return keys;
  const t = payload.table;
  if (t === "approvals" || t === "events") {
    keys.push(["audit", "decisions"], ["audit", "events"]);
  }
  if (t === "cross_dept_requests") {
    keys.push(["audit", "routing"], ["audit", "events"]);
  }
  if (
    t === "vendor_ledger" ||
    t === "contractor_ledger" ||
    t === "infra_ledger" ||
    t === "revenue_ledger"
  ) {
    keys.push(["cfo", "snapshot"]);
  }
  // Phase 1.5.A — work_items + active_sessions tables
  if (t === "work_items") {
    keys.push(["coo", "state"], ["work-items"], ["audit", "events"]);
    // Per-tenant slice if tenant_id present
    if (payload.tenant_id) {
      keys.push(["client", payload.tenant_id, "snapshot"]);
      keys.push(["client", payload.tenant_id, "work-items"]);
    }
  }
  if (t === "active_sessions") {
    keys.push(["sessions", "active"]);
  }
  if (t === "input_requests") {
    keys.push(["coo", "state"], ["inputs", "pending"]);
  }
  return keys;
}

// Phase 1.5.B — describe new event types added by /api/work-items endpoints
function describeWorkItemEvent(payload: any): { title: string; description?: string } | null {
  if (!payload || typeof payload !== "object") return null;
  const t = payload.type || payload.event_type;
  if (t === "work_item.state_changed") {
    const from = payload.from_status || "?";
    const to = payload.to_status || "?";
    const tenant = payload.tenant_id || payload.tenant;
    let title = `Work item ${from} → ${to}`;
    if (to === "obsolete" || to === "closed" || to === "cancelled") {
      title = `Work item closed (${to})`;
    }
    return {
      title,
      description: tenant ? `tenant: ${tenant}` : payload.id || undefined,
    };
  }
  if (t === "work_item.created") {
    return {
      title: "New work item",
      description: payload.tenant_id ? `tenant: ${payload.tenant_id}` : undefined,
    };
  }
  if (t === "work_item.updated") {
    return {
      title: "Work item updated",
      description: payload.tenant_id ? `tenant: ${payload.tenant_id}` : undefined,
    };
  }
  if (t === "input_request.created") {
    return {
      title: "🆕 CEO input requested",
      description: payload.tenant_id ? `tenant: ${payload.tenant_id}` : undefined,
    };
  }
  return null;
}

/**
 * Subscribe to /sse/cockpit and invalidate React Query cache on `data-changed` events.
 * Auto-reconnect with exponential backoff (max 30s).
 * Returns connection status for UI indicator.
 *
 * Phase 1.5.D channel filtering:
 *   - Cockpit (CEO scope): omit channels → wildcard (sees all events).
 *   - Per-tenant client dashboard: pass channels={["tenant:rentser"]} → only that tenant's events.
 *   - Dept dashboard: pass channels={["dept:cfo","role:ceo"]} → dept-relevant + CEO directives.
 *
 * Server-side DO routes via channel intersection. See worker/index.ts CockpitBroadcast.
 */
export function useSSE(options?: { channels?: string[] }) {
  const [status, setStatus] = useState<SSEStatus>("connecting");
  const [lastEventAt, setLastEventAt] = useState<number | null>(null);
  const queryClient = useQueryClient();
  const reconnectAttempts = useRef(0);
  const esRef = useRef<EventSource | null>(null);
  const channelsKey = (options?.channels || []).join(",");

  useEffect(() => {
    let stopped = false;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;

    const connect = () => {
      if (stopped) return;

      setStatus(reconnectAttempts.current === 0 ? "connecting" : "reconnecting");

      // Phase 1.5.D — channels filter (server-side DO routes by intersection)
      // Cockpit default: no channels → wildcard, sees all events (CEO scope).
      // Per-tenant dashboard: pass channels=["tenant:rentser"] in useSSE options.
      const channels = options?.channels;
      const channelsParam = channels && channels.length > 0
        ? `&channels=${encodeURIComponent(channels.join(","))}`
        : "";
      const url = `${API_BASE}/sse/cockpit?token=${encodeURIComponent(API_TOKEN)}${channelsParam}`;
      const es = new EventSource(url);
      esRef.current = es;

      es.addEventListener("connected", () => {
        reconnectAttempts.current = 0;
        setStatus("connected");
        setLastEventAt(Date.now());
      });

      es.addEventListener("ping", () => {
        setLastEventAt(Date.now());
      });

      es.addEventListener("data-changed", (event: MessageEvent) => {
        setLastEventAt(Date.now());
        let payload: any = null;
        try {
          payload = JSON.parse(event.data);
        } catch {
          // payload unparseable, just invalidate the broad keys
        }
        // Phase 1.5.B — Work-item / state-change events use `type` field instead of `table`/`action`.
        // Normalize: if payload has `type` matching work_item.* / input_request.*, derive `table`.
        if (payload && typeof payload === "object" && payload.type && !payload.table) {
          if (payload.type.startsWith("work_item.")) payload.table = "work_items";
          else if (payload.type.startsWith("input_request.")) payload.table = "input_requests";
          else if (payload.type.startsWith("approval.")) payload.table = "approvals";
          else if (payload.type.startsWith("cross_dept.")) payload.table = "cross_dept_requests";
        }

        // Targeted invalidation: dashboard + audit/cfo subtrees as appropriate.
        // Falls back to dashboard-only if payload is unparseable.
        for (const queryKey of affectedQueryKeys(payload)) {
          queryClient.invalidateQueries({ queryKey });
        }
        // Show toast for human-readable summary (legacy + Phase 1.5 work_item events)
        const desc = describeEvent(payload) || describeWorkItemEvent(payload);
        if (desc) {
          toast(desc.title, { description: desc.description });
        }
      });

      es.onerror = () => {
        if (stopped) return;
        es.close();
        setStatus("reconnecting");

        // Exponential backoff: 1s, 2s, 4s, ..., max 30s
        const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000);
        reconnectAttempts.current += 1;
        reconnectTimer = setTimeout(connect, delay);
      };
    };

    connect();

    return () => {
      stopped = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (esRef.current) {
        esRef.current.close();
        esRef.current = null;
      }
      setStatus("disconnected");
    };
    // Reconnect when channels change (per-tenant dashboard switches tenant)
  }, [queryClient, channelsKey]);

  return { status, lastEventAt };
}
